import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './ContentForm.css';

const ContentForm = ({ content, onSave, onCancel }) => {
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Состояние формы с значениями по умолчанию
  const [formData, setFormData] = useState({
    title: '',
    // previewText: '', // Удаляем previewText
    description: '',
    imageUrl: '',
    price: 0,
    oldPrice: '',
    isProtected: false,
    isFree: false,
    isAnnouncement: false,
    tags: []
  });
  
  // Состояние загрузки изображения
  const [imageLoading, setImageLoading] = useState(false);
  
  // Загрузка тегов при монтировании компонента
  useEffect(() => {
    const loadTags = async () => {
      try {
        const response = await fetch('/api/tags', {
          headers: {
            'X-Telegram-ID': user?.telegramId || localStorage.getItem('telegram_id')
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setTags(Array.isArray(data) ? data : data.tags || []);
        }
      } catch (error) {
        console.error('Ошибка при загрузке тегов:', error);
        setError('Не удалось загрузить теги');
      }
    };
    
    loadTags();
  }, [user]);
  
  // Если передан существующий контент, заполняем форму его данными
  useEffect(() => {
    if (content) {
      // Обрабатываем теги - если они приходят как объекты, извлекаем только ID
      const tagIds = content.tags ? content.tags.map(tag => {
        // Если тег - объект, берем его _id, иначе считаем что это уже ID
        return typeof tag === 'object' ? tag._id : tag;
      }) : [];
      
      setFormData({
        title: content.title || '',
        // previewText: content.previewText || '', // Удаляем previewText
        description: content.description || '',
        imageUrl: content.imageUrl || '',
        price: content.price || 0,
        oldPrice: content.oldPrice || '',
        isProtected: content.isProtected || false,
        isFree: content.isFree || false,
        isAnnouncement: content.isAnnouncement || false,
        tags: tagIds
      });
    }
  }, [content]);
  
  // Обработчик изменения полей формы
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Для чекбоксов используем checked, для остальных полей - value
    const newValue = type === 'checkbox' ? checked : value;
    
    // Для isFree чекбокса обновляем также цену
    if (name === 'isFree' && checked) {
      setFormData({
        ...formData,
        [name]: newValue,
        price: 0
      });
    } else {
      setFormData({
        ...formData,
        [name]: newValue
      });
    }
  };
  
  // Обработчик изменения тегов (множественный выбор)
  const handleTagsChange = (e) => {
    const options = e.target.options;
    const selectedTags = [];
    
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedTags.push(options[i].value);
      }
    }
    
    setFormData({
      ...formData,
      tags: selectedTags
    });
  };
  
  // Обработчик изменения изображения
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Начинаем загрузку и показываем индикатор
    setImageLoading(true);
    
    try {
      // Создаем FormData для отправки файла
      const formData = new FormData();
      formData.append('image', file);
      
      // Получаем Telegram ID пользователя
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      // Отправляем запрос на сервер для загрузки и оптимизации изображения
      const response = await fetch('/api/images/upload', {
        method: 'POST',
        headers: {
          'X-Telegram-ID': telegramId
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Не удалось загрузить изображение');
      }
      
      const result = await response.json();
      console.log('Изображение загружено:', result);
      
      // Обновляем состояние с URL загруженного изображения
      setFormData({
        ...formData,
        imageUrl: result.image.url
      });
      
    } catch (error) {
      console.error('Ошибка при загрузке изображения:', error);
      alert('Не удалось загрузить изображение. Пожалуйста, попробуйте еще раз.');
    } finally {
      setImageLoading(false);
    }
  };
  
  // Обработчик отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Создаем данные для отправки
      const contentData = {
        ...formData,
        // Если материал бесплатный, устанавливаем цену в 0
        price: formData.isFree ? 0 : parseFloat(formData.price)
      };
      
      // Если старая цена пустая, удаляем ее
      if (!contentData.oldPrice) {
        delete contentData.oldPrice;
      } else {
        contentData.oldPrice = parseFloat(contentData.oldPrice);
      }
      
      // Вызываем функцию сохранения, переданную через props
      await onSave(contentData);
    } catch (error) {
      console.error('Ошибка при сохранении контента:', error);
      setError('Не удалось сохранить контент. Пожалуйста, попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="content-form-container">
      <h2>{content ? 'Редактирование контента' : 'Создание нового контента'}</h2>
      
      {error && <div className="form-error">{error}</div>}
      
      <form onSubmit={handleSubmit} className="content-form">
        {/* Изображение */}
        <div className="form-group">
          <label>Изображение (обложка):</label>
          <div className="image-upload-container">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageChange} 
              className="image-upload-input"
            />
            {formData.imageUrl && !imageLoading && (
              <div className="image-preview card-style">
                <img src={formData.imageUrl} alt="Preview" />
              </div>
            )}
            {imageLoading && (
              <div className="image-loading">
                <div className="loading-spinner"></div>
                <p>Загрузка изображения...</p>
              </div>
            )}
            {!formData.imageUrl && !imageLoading && (
              <div className="image-upload-placeholder">
                Нажмите для добавления изображения
              </div>
            )}
          </div>
        </div>
        
        {/* Заголовок */}
        <div className="form-group">
          <label htmlFor="title">Заголовок:</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="Введите заголовок"
          />
        </div>
        
        {/* Полное описание */}
        <div className="form-group">
          <label htmlFor="description">Полное описание:</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Введите полное описание. Можно использовать HTML-теги, например, <b> для жирного текста или <i> для курсива."
            rows="5" 
          />
          <small>Поддерживаются HTML-теги: <b>жирный</b>, <i>курсив</i>, <br />перенос строки.</small>
        </div>
        
        {/* Цена */}
        <div className="form-group form-row">
          <div className="form-col">
            <label htmlFor="price">Цена (₽):</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              disabled={formData.isFree}
              min="0"
              step="1"
            />
          </div>
          
          <div className="form-col">
            <label htmlFor="oldPrice">Старая цена (₽):</label>
            <input
              type="number"
              id="oldPrice"
              name="oldPrice"
              value={formData.oldPrice}
              onChange={handleChange}
              disabled={formData.isFree}
              min="0"
              step="1"
              placeholder="Опционально"
            />
          </div>
        </div>
        
        {/* Выбор тегов */}
        <div className="form-group">
          <label htmlFor="tags">Теги:</label>
          <select
            id="tags"
            name="tags"
            multiple
            value={formData.tags}
            onChange={handleTagsChange}
            className="tags-select"
          >
            {tags.map(tag => (
              <option key={tag._id || tag.id} value={tag._id || tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
          <small>Удерживайте Ctrl (Cmd на Mac) для выбора нескольких тегов</small>
        </div>
        
        {/* Чекбоксы */}
        <div className="form-group checkbox-group">
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="isFree"
              name="isFree"
              checked={formData.isFree}
              onChange={handleChange}
            />
            <label htmlFor="isFree">Бесплатный контент</label>
          </div>
          
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="isProtected"
              name="isProtected"
              checked={formData.isProtected}
              onChange={handleChange}
            />
            <label htmlFor="isProtected">Защищенный контент (запрет пересылки)</label>
          </div>
          
          <div className="checkbox-item">
            <input
              type="checkbox"
              id="isAnnouncement"
              name="isAnnouncement"
              checked={formData.isAnnouncement}
              onChange={handleChange}
            />
            <label htmlFor="isAnnouncement">Анонс</label>
          </div>
        </div>
        
        {/* Кнопки действий */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="admin-button"
            disabled={loading}
          >
            Отмена
          </button>
          <button 
            type="submit" 
            className="admin-button"
            disabled={loading}
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContentForm;
